import { iaRa } from 'lib/helpers/utility';
import numOrDefault from 'lib/helpers/numOrDefault';

import { money, neg } from './invoiceDocModel';

const AGING_LABELS = {
  'Current 0-30': 'Current 0–30',
  '31-60 Days': '31–60 days',
  '61-90 Days': '61–90 days',
  '91+ Days': '91+ days'
};

const sumLines = (rows, key) =>
  iaRa(rows).reduce((sum, item) => sum + numOrDefault(item?.[key]), 0);

export const buildSummaryRows = ({ totals, balances, techFee, rows: lineRows }) => {
  const rows = [];
  const confirmed = numOrDefault(balances?.confirmed);
  const gfe = numOrDefault(balances?.gfe);
  const combined = !!balances?.combined;

  if (combined) {
    rows.push({ label: 'Confirmed patient balance', value: money(confirmed) });
    rows.push({ label: 'Estimated patient balance (GFE)', value: money(gfe) });
  } else {
    rows.push({
      label: balances?.isGfe ? 'Estimated patient balance' : 'Patient balance',
      value: money(confirmed + gfe)
    });
  }

  const discount = numOrDefault(totals?.discount);
  const writeOff = numOrDefault(totals?.write_off);
  const otherAdj = numOrDefault(totals?.other_adj);
  const adjustments = discount + writeOff + otherAdj;

  if (adjustments) {
    rows.push({ label: 'Adjustments & discounts', value: neg(adjustments) });
    if (discount) rows.push({ label: 'Additional discount', value: neg(discount), muted: true });
    if (writeOff) rows.push({ label: 'Write-off', value: neg(writeOff), muted: true });
    if (otherAdj) rows.push({ label: 'Other adjustments', value: neg(otherAdj), muted: true });
  }

  const stateTax = sumLines(lineRows, 'state_tax');
  const localTax = sumLines(lineRows, 'local_tax');
  const tax = stateTax + localTax || numOrDefault(totals?.tax);

  if (tax) {
    const breakdown = [
      stateTax ? `state ${money(stateTax)}` : '',
      localTax ? `local ${money(localTax)}` : ''
    ].filter(Boolean);

    rows.push({
      label: breakdown.length ? `Tax included · ${breakdown.join(' · ')}` : 'Tax included',
      value: money(tax)
    });
  }

  const fee = numOrDefault(techFee);
  if (fee) rows.push({ label: 'Technology fee', value: money(fee) });

  const amountPaid = numOrDefault(totals?.amount_paid);
  if (amountPaid) rows.push({ label: 'Payments', value: neg(amountPaid) });

  return rows;
};

export const buildSummaryFootnote = ({ totals, isGfe }) => {
  const insBalance = numOrDefault(totals?.ins_balance);

  if (!insBalance) return null;

  return {
    label: `${isGfe ? 'Est. insurance balance' : 'Insurance balance'} · billed to your plan`,
    value: money(insBalance)
  };
};

export const buildAging = (aging) => {
  const entries = Object.entries(aging || {});

  if (!entries.length) return null;

  return {
    buckets: entries.map(([bucket, amount]) => ({
      key: bucket,
      label: AGING_LABELS[bucket] || bucket,
      value: money(amount)
    })),
    total: money(entries.reduce((sum, [, amount]) => sum + numOrDefault(amount), 0))
  };
};

/** The fine print that closes every page, per templates 7a–7f. */
export const buildFooterLegal = ({ practiceName, invoiceNumber, variantLabel, dueDate }) => {
  const parts = [
    `© ${new Date().getFullYear()} ${practiceName}`,
    'Powered by Myriad Health',
    [invoiceNumber ? `Invoice ${invoiceNumber}` : '', variantLabel].filter(Boolean).join(' – ')
  ].filter(Boolean);

  const line = parts.join(' · ');

  return dueDate ? `${line} · patient balance due ${dueDate}.` : `${line}.`;
};

export const sumBalances = (rows, predicate) =>
  iaRa(rows)
    .filter(predicate)
    .reduce((sum, item) => sum + numOrDefault(item?.ptBalance), 0);
